import type { AutoUpdateSettings } from "../types/electron/autoUpdateSettingsMethods";
import type { DatarouterConfig } from "../types/electron/datarouterMethods";
import type { FeedSettings } from "../types/electron/feedSettingsMethods";
import type { GamepadSettings } from "../types/electron/gamepadTypes";
import type { KeyboardSettings } from "../types/electron/keyboardTypes";
import type { NotificationSettings } from "../types/electron/notificationSettingsMethods";
import type { VideoSettings } from "../types/electron/videoSettingsMethods";

export const FEED_RATE_UNITS = [
  "g/s",
  "kg/min",
  "kg/ton/min",
  "p/fish/min",
] as const;
export const KEYBOARD_PRESETS = ["akva", "scale", "custom"] as const;
export const ALERT_TYPES = ["ROW", "TOAST"] as const;
export const RENDER_DETECTION_MODES = ["PELLETS", "ALL", "NONE"] as const;
export const CAM_IN_CAM_SIZES = ["small", "medium", "large"] as const;
export const SENSITIVITY_LEVELS = ["HIGH", "MEDIUM", "LOW"] as const;
export const GAMEPAD_MODES = ["press", "hold"] as const;
export const GAMEPAD_DIRECTIONS = ["positive", "negative"] as const;
export const QUICK_ACTION_TARGET_TYPES = ["camera", "cage"] as const;

export const FEED_LIST_VOCABULARIES = {
  columns: [
    "unit",
    "line",
    "lineStatus",
    "feedMode",
    "cameraDepth",
    "livePellets",
    "oxygen",
    "temperature",
    "pipePressure",
    "pipePressureGraph",
    "waterFlow",
    "visitGrams",
    "feedAmount",
    "totalVisits",
    "feedGraph",
    "feedRate",
    "copilot",
    "startPause",
    "utility",
  ],
  copilotColumns: [
    "unit",
    "line",
    "lineStatus",
    "feedMode",
    "strategy",
    "scenario",
    "nextScenario",
    "af",
    "cameraDepth",
    "livePellets",
    "oxygen",
    "temperature",
    "pipePressure",
    "pipePressureGraph",
    "waterFlow",
    "visitGrams",
    "feedAmount",
    "totalVisits",
    "feedGraph",
    "feedRate",
    "copilot",
  ],
  filters: [
    "currentlyFeeding",
    "notFeeding",
    "manual",
    "planner",
    "schedule",
    "disabled",
    "blowAirEnabled",
    "blowAirDisabled",
    "copilotEnabled",
    "copilotDisabled",
  ],
} as const;

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings &
  Record<string, unknown> = {
  "feedProfile.pelletAlerts.enabled": true,
  "feedProfile.pelletAlerts.alertType": "ROW",
  "feedProfile.pelletAlerts.alertSound": "BELL",
  "feedProfile.pelletAlerts.sensitivity": 6,
  "feedProfile.pelletAlerts.isCritical": true,
  "equipment.winch.enabled": true,
  "equipment.winch.alertType": "ROW",
  "equipment.winch.alertSound": "BELL",
  "equipment.winch.isCritical": true,
  "equipment.winchManualControl.enabled": true,
  "equipment.winchManualControl.alertType": "ROW",
  "equipment.winchManualControl.alertSound": "BELL",
  "equipment.winchManualControl.isCritical": true,
  "equipment.feedSystem.enabled": true,
  "equipment.feedSystem.alertType": "ROW",
  "equipment.feedSystem.alertSound": "BELL",
  "equipment.feedSystem.isCritical": true,
  "equipment.airPressure.enabled": true,
  "equipment.airPressure.alertType": "ROW",
  "equipment.airPressure.alertSound": "BELL",
  "equipment.airPressure.isCritical": true,
  muteForToday: false,
  "feeding.overfeeding.feedingResponse.enabled": false,
  "feeding.overfeeding.feedingResponse.alertType": "ROW",
  "feeding.overfeeding.feedingResponse.alertSound": "BELL",
  "feeding.overfeeding.feedingResponse.sensitivity": "LOW",
  "feeding.overfeeding.feedingResponse.isCritical": false,
  "feeding.underfeeding.limitGrazing.enabled": false,
  "feeding.underfeeding.limitGrazing.alertType": "ROW",
  "feeding.underfeeding.limitGrazing.alertSound": "BELL",
  "feeding.underfeeding.limitGrazing.sensitivity": "LOW",
  "feeding.underfeeding.limitGrazing.isCritical": false,
  "feeding.standardization.feedProfileSmoothness.enabled": false,
  "feeding.standardization.feedProfileSmoothness.alertType": "ROW",
  "feeding.standardization.feedProfileSmoothness.alertSound": "BELL",
  "feeding.standardization.feedProfileSmoothness.sensitivity": "LOW",
  "feeding.standardization.feedProfileSmoothness.isCritical": false,
  "feeding.standardization.cameraMovement.enabled": false,
  "feeding.standardization.cameraMovement.alertType": "ROW",
  "feeding.standardization.cameraMovement.alertSound": "BELL",
  "feeding.standardization.cameraMovement.sensitivity": "LOW",
  "feeding.standardization.cameraMovement.isCritical": false,
  "copilot.automaticChanges.enabled": true,
  "copilot.automaticChanges.alertType": "ROW",
  "copilot.automaticChanges.alertSound": "BELL",
  "copilot.automaticChanges.isCritical": false,
  "copilot.quickActions.failedToExecute.enabled": true,
  "copilot.quickActions.failedToExecute.alertType": "ROW",
  "copilot.quickActions.failedToExecute.alertSound": "BELL",
  "copilot.quickActions.failedToExecute.isCritical": false,
  "environment.oxygen.enabled": false,
  "environment.oxygen.alertType": "ROW",
  "environment.oxygen.alertSound": "BELL",
  "environment.oxygen.isCritical": false,
  "environment.oxygenSaturation.enabled": false,
  "environment.oxygenSaturation.alertType": "ROW",
  "environment.oxygenSaturation.alertSound": "BELL",
  "environment.oxygenSaturation.isCritical": false,
  "environment.oxygenForecast.enabled": false,
  "environment.oxygenForecast.alertType": "ROW",
  "environment.oxygenForecast.alertSound": "BELL",
  "environment.oxygenForecast.isCritical": false,
  "environment.saturationTemperatureModel.norway.enabled": false,
  "environment.saturationTemperatureModel.norway.alertType": "ROW",
  "environment.saturationTemperatureModel.norway.alertSound": "BELL",
  "environment.saturationTemperatureModel.norway.isCritical": false,
  "environment.saturationTemperatureModel.chile.enabled": false,
  "environment.saturationTemperatureModel.chile.alertType": "ROW",
  "environment.saturationTemperatureModel.chile.alertSound": "BELL",
  "environment.saturationTemperatureModel.chile.isCritical": false,
  "environment.temperature.enabled": false,
  "environment.temperature.alertType": "ROW",
  "environment.temperature.alertSound": "BELL",
  "environment.temperature.isCritical": false,
  "environment.salinity.enabled": false,
  "environment.salinity.alertType": "ROW",
  "environment.salinity.alertSound": "BELL",
  "environment.salinity.isCritical": false,
  "feedProfile.pelletAlerts.actionType": "no_action",
  "feeding.overfeeding.feedingResponse.actionType": "no_action",
  "feeding.underfeeding.limitGrazing.actionType": "no_action",
  "feeding.standardization.feedProfileSmoothness.actionType": "no_action",
  "feeding.standardization.cameraMovement.actionType": "no_action",
  "equipment.camera.enabled": false,
  "equipment.camera.alertType": "ROW",
  "equipment.camera.alertSound": "BELL",
  "equipment.camera.isCritical": false,
  "equipment.camera.actionType": "no_action",
  "equipment.winchManualControl.actionType": "reset_winch_lockout",
  "equipment.feedSystem.actionType": "no_action",
  "equipment.airPressure.actionType": "no_action",
  "copilot.automaticChanges.actionType": "no_action",
  "copilot.quickActions.failedToExecute.actionType": "no_action",
  "environment.oxygen.thresholdOperator": "GT",
  "environment.oxygen.thresholdValue": "",
  "environment.oxygen.actionType": "no_action",
  "environment.oxygenForecast.actionType": "no_action",
  "environment.temperature.thresholdOperator": "GT",
  "environment.temperature.thresholdValue": "",
  "environment.temperature.actionType": "no_action",
  "environment.salinity.thresholdOperator": "GT",
  "environment.salinity.thresholdValue": "",
  "environment.salinity.actionType": "no_action",
  "environment.oxygenSaturation.thresholdOperator": "GT",
  "environment.oxygenSaturation.thresholdValue": "",
  "environment.oxygenSaturation.actionType": "no_action",
};

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  defaults: {
    defaultInfoRows: ["activity", "pellets", "feedkg"],
    defaultTimeRangeSeconds: 15 * 60,
    camInCamSize: "small",
    defaultRenderDetections: "PELLETS",
  },
  infoRowOverrides: {},
};

export const DEFAULT_DATAROUTER_CONFIG: DatarouterConfig = {
  enabled: false,
  connectsToCloud: false,
  ip: "127.0.0.1",
  port: "8180",
};

export const DEFAULT_AUTO_UPDATE_SETTINGS: AutoUpdateSettings = {
  enabled: true,
};

export const DEFAULT_FEED_SETTINGS: FeedSettings = {
  manualFeedingIncrement: 0.1,
  manualAppetiteIncrement: 0.1,
  feedRateUnit: "kg/min",
};

export const DEFAULT_GAMEPAD_SETTINGS: GamepadSettings = {
  enabled: false,
  bindings: null,
};

export const DEFAULT_KEYBOARD_SETTINGS: KeyboardSettings = {
  preset: "akva",
  customBindings: null,
};
